import type { RefObject } from 'react';
import LocationActive from '~/icons/location-active.svg';
import Location from '~/icons/location.svg';
import type { Store } from '~/lib/data-contract/salesforce';
import { SalesforceProduct } from '~/lib/salesforce/types';
import Spinner from '~/shared/components/Spinner/Spinner';
import { StoreSearchInput } from '~/shared/components/StoreSearchInput';
import { ToggleSwitch } from '~/shared/components/Switch';
import { Typography } from '~/shared/components';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ReservationDrawer.module.scss';
import { ReservationStoreList } from './ReservationStoreList';
import type { ReservationStoreCtaIntent } from './types';

type StoreListStepProps = {
    product: SalesforceProduct;
    isStoresWithStockOnly: boolean;
    setIsStoresWithStockOnly: (isStoresWithStockOnly: boolean) => void;
    searchQuery: string;
    setSearchQuery: (searchQuery: string) => void;
    storeSearchInputRef: RefObject<HTMLInputElement>;
    isLoadingStores: boolean;
    isGeoActive: boolean;
    isLocating: boolean;
    toggleGeoLocation: () => void;
    searchError: string;
    nearbyStores?: Store[];
    quantity: number;
    pendingStoreCtaId: string | null;
    onStoreCtaClick: (intent: ReservationStoreCtaIntent) => void;
    onAccordionOpen: (shopId: string, index: number) => void;
};

export const StoreListStep = ({
    product,
    isStoresWithStockOnly,
    setIsStoresWithStockOnly,
    searchQuery,
    setSearchQuery,
    storeSearchInputRef,
    isLoadingStores,
    isGeoActive,
    isLocating,
    toggleGeoLocation,
    searchError,
    nearbyStores,
    pendingStoreCtaId,
    quantity,
    onStoreCtaClick,
    onAccordionOpen,
}: StoreListStepProps) => {
    const { translate } = useTranslation();
    const storesWithStockCount = product?.c_storeIdsWithStock?.length ?? 0;

    return (
        <>
            <section className={styles.storeSearchWrapper}>
                <div className={styles.storeSearchHeader}>
                    <Typography as="h3" weight="medium" variant="paragraph-S">
                        På lager i {storesWithStockCount} butikker
                    </Typography>
                    <ToggleSwitch
                        label={translate('message.onlyStoresWithStock')}
                        id="show-store-with-stock"
                        checked={isStoresWithStockOnly}
                        onCheckedChange={setIsStoresWithStockOnly}
                    />
                </div>
                <div className={styles.storeSearchActions}>
                    <div className={styles.storeSearchInput}>
                        <StoreSearchInput
                            inputRef={storeSearchInputRef}
                            value={searchQuery}
                            setValue={setSearchQuery}
                        />
                    </div>
                    <button
                        aria-label={translate('findStore.closeToMyLocation')}
                        aria-pressed={isGeoActive}
                        aria-busy={isLocating}
                        className={styles.locationButton}
                        type="button"
                        disabled={isLocating}
                        onClick={toggleGeoLocation}
                    >
                        {isLocating ? <Spinner /> : isGeoActive ? <LocationActive /> : <Location />}
                    </button>
                </div>
            </section>

            <ReservationStoreList
                product={product}
                isStoresWithStockOnly={isStoresWithStockOnly}
                searchError={searchError}
                isFetching={isLoadingStores}
                storesList={nearbyStores}
                quantity={quantity}
                pendingStoreCtaId={pendingStoreCtaId}
                onStoreCtaClick={onStoreCtaClick}
                onAccordionOpen={onAccordionOpen}
            />
        </>
    );
};
